import { isNumber } from 'lodash';
import { IHistoryItem } from '@/framework';
import { Framework } from '@/framework/instance';
import { ILayerProps } from '@/version-types';
import { BackgroundLayer } from '.';
import { HistoryType, IBatchUpdateBackgroundData, IConvertBackgroundToImageData } from './type';

export class BackgroundHistory extends Framework {
    constructor(identity: string) {
        super(identity);
        this.registerConvertBackgroundToImageHistory();
        this.registerBatchUpdateBackgroundHistory();
    }

    private registerBatchUpdateBackgroundHistory() {
        this.framework.history.register(HistoryType.BATCH_UPDATE_BACKGROUND, {
            onRedoUndo: (historyItem: IHistoryItem<IBatchUpdateBackgroundData>, isRedo: boolean, isUndo: boolean) => {
                if (historyItem.isDirty) return;
                const { newLayers, oldLayers } = historyItem.data.data;
                if (isRedo) {
                    this.framework.stage.batchUpdate(newLayers, true);
                }
                if (isUndo) {
                    this.framework.stage.batchUpdate(oldLayers, true);
                }
            },
            onAfterWrite: (historyItem: IHistoryItem<IBatchUpdateBackgroundData>) => {
                return {
                    message: [historyItem.data.data.newLayers],
                };
            },
            onAfterRedoUndo: (historyItem: IHistoryItem<IBatchUpdateBackgroundData>, isRedo) => {
                return {
                    message: [isRedo ? historyItem.data.data.newLayers : historyItem.data.data.oldLayers],
                };
            },
            onBeforeRedoUndo: (historyItem: IHistoryItem<IBatchUpdateBackgroundData>, isRedo, isUndo, store) => {
                const { oldLayers } = historyItem.data.data;
                if (isUndo) {
                    const oldFlatLayers: ILayerProps[] = [];
                    const newFlatLayers: ILayerProps[] = [];
                    for (const layer of oldLayers) {
                        const latestLayer = this.framework.getLayer(layer.id, true);
                        if (latestLayer) {
                            oldFlatLayers.push(layer);
                            newFlatLayers.push(latestLayer);
                        }
                    }
                    historyItem.isDirty = !newFlatLayers.length;
                    store.set(historyItem.cursor, {
                        isDirty: historyItem.isDirty,
                        oldFlatLayers,
                        newFlatLayers,
                    });
                }
                if (isRedo) {
                    const beforeData = store.get(historyItem.cursor);
                    if (!beforeData) {
                        historyItem.isDirty = true;
                        return;
                    }
                    historyItem.isDirty = beforeData.isDirty;
                    const data = historyItem.data.data;
                    const { oldFlatLayers, newFlatLayers } = beforeData;
                    data.oldLayers = oldFlatLayers;
                    data.newLayers = newFlatLayers;
                }
            },
            onMessage: (message: [ILayerProps[]]) => {
                this.framework.stage.batchUpdate(message[0], true);
            },
        });
    }

    private registerConvertBackgroundToImageHistory() {
        this.framework.history.register(HistoryType.CONVERT_BACKGROUND_TO_IMAGE, {
            onRedoUndo: (
                historyItem: IHistoryItem<IConvertBackgroundToImageData>,
                isRedo: boolean,
                isUndo: boolean,
            ) => {
                if (historyItem.isDirty) return;
                const { backgroundLayer, imageLayer, bgOptions } = historyItem.data.data;
                if (isRedo) {
                    const originIndex = this.framework.tryGetLayerOriginIndex(imageLayer.id);
                    if (!isNumber(originIndex)) return;
                    const info = this.framework.tryGetOriginIdsAndIndex(originIndex, true);
                    const originIds = info.originIds;
                    originIds.splice(1, 0, imageLayer.id);
                    const currentLayer = this.framework.getBackgroundLayer(originIndex) || undefined;
                    this.framework.getInstance(BackgroundLayer).update(bgOptions, false, '', currentLayer);
                    this.framework.stage.addOrUpdate(imageLayer, true, originIndex);
                }
                if (isUndo) {
                    this.framework.stage.addOrUpdate(backgroundLayer);
                    this.framework.operate.delLayer(imageLayer.id);
                    this.framework.operate.delActiveLayer();
                }
            },
            onAfterWrite: (historyItem: IHistoryItem<IConvertBackgroundToImageData>) => {
                return {
                    message: [historyItem.data.data],
                };
            },
            onAfterRedoUndo: (historyItem: IHistoryItem<IConvertBackgroundToImageData>) => {
                return {
                    message: [historyItem.data.data],
                };
            },
            onBeforeRedoUndo: (historyItem: IHistoryItem<IConvertBackgroundToImageData>, isRedo, isUndo, store) => {
                if (isRedo) {
                    const layer = this.framework.getLayer(historyItem.data.data.imageLayer.id);
                    if (layer) {
                        historyItem.isDirty = true;
                    }
                }
            },
            onMessage: (message: [IConvertBackgroundToImageData], isRedo, isUndo, isWrite) => {
                const { backgroundLayer, imageLayer, bgOptions } = message[0];
                if (isRedo || isWrite) {
                    const originIndex = this.framework.tryGetLayerOriginIndex(imageLayer.id);
                    if (!isNumber(originIndex)) return;
                    const info = this.framework.tryGetOriginIdsAndIndex(originIndex, true);
                    const originIds = info.originIds;
                    originIds.splice(1, 0, imageLayer.id);
                    const currentLayer = this.framework.getBackgroundLayer(originIndex) || undefined;
                    this.framework.getInstance(BackgroundLayer).update(bgOptions, false, '', currentLayer);
                    this.framework.stage.addOrUpdate(imageLayer, true, originIndex);
                }
                if (isUndo) {
                    this.framework.operate.tryRemoveActiveLayer(imageLayer.id);
                    this.framework.stage.addOrUpdate(backgroundLayer);
                    this.framework.operate.delLayer(imageLayer.id);
                }
            },
        });
    }
}
